/**
 * The public pages' language addresses inside the app (`PAGE_LOCALES` in seo/site.ts): English at
 * the root (`/login`), Turkish under `/tr/` (`/tr/login`). The prerender renders both, so these
 * read the router's location only, never the browser, and the first render matches either way.
 */

import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { i18n, normalizeLocale } from '@/i18n';

import { isPublicPath, localePath, pathLocale } from './site';

/**
 * Links between the public pages keep the address's language: on `/tr/…`, `/login` is
 * `/tr/login`. Every other path (the app behind sign-in) and every root address is left as it is.
 */
export function useLocalizedPath(): (path: string) => string {
  const locale = pathLocale(useLocation().pathname);
  return useCallback(
    (path: string) => (locale && isPublicPath(path) ? localePath(path, locale) : path),
    [locale],
  );
}

/**
 * Coming to a language address inside the app (Back to `/tr/` after switching to English there)
 * switches to its language, as opening it does (`detectLocale()`). Not saved: only a choice in
 * the language menu is. Mount once, from App.tsx.
 */
export function useAddressLocale(): void {
  const locale = pathLocale(useLocation().pathname);

  useEffect(() => {
    if (!locale) return;
    if (normalizeLocale(i18n.resolvedLanguage ?? i18n.language) === locale) return;
    void i18n.changeLanguage(locale);
  }, [locale]);
}
